import type { AnyAppData } from '@/server/app-data';
import {
  CSVSeparatorsMapped,
  FILTER_MAPPING,
  DATE_TABLE_DATA_IDS,
  DATETIME_TABLE_DATA_IDS,
  RICH_TABLE_DATA_IDS,
  SANITIZE_TABLE_DATA_IDS,
  PRICE_TABLE_DATA_IDS,
  type CSVSeparator,
} from './constants';

import { toFixed } from '@repo/utils/calc';

import moment from 'moment';
import 'moment/locale/fi';

type CSVRow = { fields: string[]; data: Record<string, unknown> };

function _writeCSV(
  rows: CSVRow[],
  headers: string[],
  separator: string,
  productPageData: AnyAppData
) {
  const headerRow = headers.length ? [headers.join(separator)] : [];
  return rows
    .reduce((acc, row) => {
      const newRow = row.fields.map((field) => {
        const fieldValue =
          typeof row.data[field] === 'undefined'
            ? ''
            : sanitizeField(row.data[field], field, productPageData);
        return String(fieldValue).replace(new RegExp(separator, 'g'), ' ');
      });
      return [...acc, newRow.join(separator)];
    }, headerRow)
    .join('\r\n');
}

type SanitizeFieldKey =
  | (typeof SANITIZE_TABLE_DATA_IDS)[number]
  | (typeof RICH_TABLE_DATA_IDS)[number]
  | (typeof PRICE_TABLE_DATA_IDS)[number]
  | (typeof DATE_TABLE_DATA_IDS)[number]
  | (typeof DATETIME_TABLE_DATA_IDS)[number];

function sanitizeField(
  value: unknown,
  key: SanitizeFieldKey | (string & {}),
  productPageData: AnyAppData
) {
  if (SANITIZE_TABLE_DATA_IDS.includes(key as (typeof SANITIZE_TABLE_DATA_IDS)[number])) {
    try {
      const strValue = String(value).replace(/<\/?[^>]+(>|$)/g, '');
      value = `"${strValue}"`;
    } catch (error) {
      console.error(error);
    }
  }
  if (RICH_TABLE_DATA_IDS.includes(key as (typeof RICH_TABLE_DATA_IDS)[number])) {
    try {
      const fieldMappingId =
        key in FILTER_MAPPING ? FILTER_MAPPING[key as keyof typeof FILTER_MAPPING] : undefined;
      const mapping =
        fieldMappingId && fieldMappingId in productPageData
          ? productPageData[fieldMappingId]
          : undefined;
      if (mapping && value && value !== null && typeof value === 'string') {
        value = value
          .split(',')
          .map((v) => (v in mapping ? mapping[v]?.name || v : v))
          .join(',');
      }
    } catch (error) {
      console.error(error);
    }
  }
  if (
    PRICE_TABLE_DATA_IDS.includes(key as (typeof PRICE_TABLE_DATA_IDS)[number]) &&
    typeof value === 'number'
  ) {
    value = toFixed(value, 2).replace('.', ',');
  }
  if (DATE_TABLE_DATA_IDS.includes(key as (typeof DATE_TABLE_DATA_IDS)[number])) {
    value = value ? moment(value).format('DD.MM.YYYY') : '—';
  }
  if (DATETIME_TABLE_DATA_IDS.includes(key as (typeof DATETIME_TABLE_DATA_IDS)[number])) {
    value = value ? moment(value).format('DD.MM.YYYY HH:mm') : '—';
  }
  return value;
}

function generateCSVData(
  rows: CSVRow[],
  headers: string[],
  separator: CSVSeparator,
  productPageData: AnyAppData
) {
  const _separator = CSVSeparatorsMapped[separator].rawValue;
  return '\ufeff' + _writeCSV(rows, headers, _separator, productPageData);
  // Todo: try 	EF BB BF \uefbbbf to fix ääkköset in ms excel
}

export { generateCSVData, sanitizeField };
